import { useRef, useState } from "react";
import { Plus, Trash2, Upload, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import {
  useAdminSponsors,
  useCreateSponsor,
  useUpdateSponsor,
  useDeleteSponsor,
  type AdminSponsor,
} from "../../lib/api/useSponsors";
import { fileToLogoDataUri } from "../../lib/img";
import { useToast } from "../../lib/toast/ToastProvider";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Field } from "../ui/Field";
import { InlineLoader } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { ConfirmDialog } from "../ui/ConfirmDialog";

/**
 * Sponsor management.
 *
 * Ordering uses up/down buttons that swap adjacent sortOrder values rather than
 * a drag handle: there are a handful of sponsors, the list is edited a few
 * times a year, and swap-on-click works with a keyboard and on a phone, which
 * dragging does not.
 */
export function SponsorsPanel() {
  const { data: sponsors, isLoading, error } = useAdminSponsors();
  const create = useCreateSponsor();
  const update = useUpdateSponsor();
  const remove = useDeleteSponsor();
  const { publish } = useToast();

  const [name, setName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminSponsor | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  if (isLoading) return <InlineLoader label="Loading sponsors…" />;
  if (error) return <Card className="p-6 text-sm text-red-700">Couldn&apos;t load sponsors.</Card>;

  const list = sponsors ?? [];

  async function pickLogo(file: File) {
    try {
      setLogoUrl(await fileToLogoDataUri(file));
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't use that image",
        description: err instanceof Error ? err.message : undefined,
      });
    }
  }

  function resetForm() {
    setName("");
    setWebsiteUrl("");
    setLogoUrl(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  /** Swaps this row's sortOrder with its neighbour's. */
  function move(index: number, direction: -1 | 1) {
    const a = list[index];
    const b = list[index + direction];
    if (!a || !b) return;
    // Both rows are written because the two may currently share a value (they
    // all default to 0), in which case swapping one alone changes nothing.
    update.mutate({ id: a.id, changes: { sortOrder: b.sortOrder } });
    update.mutate({ id: b.id, changes: { sortOrder: a.sortOrder } });
  }

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <h3 className="font-display text-base font-semibold text-neutral-900">Add a sponsor</h3>
        <form
          className="mt-4 grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(
              { name: name.trim(), logoUrl, websiteUrl: websiteUrl.trim() || null },
              {
                onSuccess: () => {
                  publish({ variant: "success", title: "Sponsor added" });
                  resetForm();
                },
                onError: (err) =>
                  publish({
                    variant: "error",
                    title: "Couldn't add that sponsor",
                    description:
                      err.code === "VALIDATION_ERROR"
                        ? "Check the name, and that the link starts with https://"
                        : undefined,
                  }),
              },
            );
          }}
        >
          <Field label="Name" htmlFor="sponsor-name">
            <input
              id="sponsor-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bright Start Childcare"
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Website (optional)" htmlFor="sponsor-url">
            <input
              id="sponsor-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              className={INPUT_CLASS}
            />
          </Field>

          <div className="sm:col-span-2">
            <span className="text-sm font-medium text-neutral-700">Logo (optional)</span>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt=""
                  className="h-10 w-auto max-w-[9rem] rounded border border-neutral-200 bg-white object-contain p-1"
                />
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void pickLogo(f);
                }}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-4 w-4" aria-hidden /> {logoUrl ? "Replace" : "Choose image"}
              </Button>
              {logoUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setLogoUrl(null)}>
                  Remove
                </Button>
              )}
              <span className="text-xs text-neutral-500">
                Resized in your browser. PNG keeps transparency.
              </span>
            </div>
          </div>

          <div className="sm:col-span-2">
            <Button type="submit" disabled={!name.trim()} isLoading={create.isPending}>
              <Plus className="h-4 w-4" aria-hidden /> Add sponsor
            </Button>
          </div>
        </form>
      </Card>

      {list.length === 0 ? (
        <EmptyState
          title="No sponsors yet"
          description="Sponsors you add appear on the landing page. The section stays hidden while this list is empty."
        />
      ) : (
        <ul className="space-y-3">
          {list.map((s, i) => (
            <li key={s.id}>
              <Card className="flex flex-wrap items-center gap-4 p-4">
                <div className="flex h-12 w-28 shrink-0 items-center justify-center rounded border border-neutral-100 bg-white">
                  {s.logoUrl ? (
                    <img
                      src={s.logoUrl}
                      alt=""
                      className="max-h-10 w-auto max-w-[6.5rem] object-contain"
                    />
                  ) : (
                    <span className="text-xs text-neutral-400">No logo</span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-neutral-900">{s.name}</p>
                  {s.websiteUrl && (
                    <a
                      href={s.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex max-w-full items-center gap-1 truncate text-xs text-sky-700 hover:underline"
                    >
                      <span className="truncate">{s.websiteUrl}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                    </a>
                  )}
                </div>

                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    s.isActive
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-neutral-200 text-neutral-700"
                  }`}
                >
                  {s.isActive ? "on the site" : "hidden"}
                </span>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Move ${s.name} earlier`}
                    disabled={i === 0 || update.isPending}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Move ${s.name} later`}
                    disabled={i === list.length - 1 || update.isPending}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => update.mutate({ id: s.id, changes: { isActive: !s.isActive } })}
                  >
                    {s.isActive ? "Hide" : "Show"}
                  </Button>
                  <Button
                    variant="destructiveOutline"
                    size="sm"
                    aria-label={`Delete ${s.name}`}
                    onClick={() => setPendingDelete(s)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this sponsor?"
        description={`${pendingDelete?.name ?? ""} will be removed from the landing page and the logo deleted. To take them off the site temporarily, use Hide instead.`}
        confirmLabel="Delete"
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          remove.mutate(pendingDelete.id, {
            onSuccess: () => {
              publish({ variant: "success", title: "Sponsor deleted" });
              setPendingDelete(null);
            },
            onError: () => publish({ variant: "error", title: "Couldn't delete that sponsor" }),
          });
        }}
      />
    </div>
  );
}
